const { parseAbiItem, decodeEventLog, decodeAbiParameters, createPublicClient, http, keccak256 } = require('viem')
const { NETWORK_CONFIG } = require('@configs')
const { ETERNAL_STORAGE_ABI, DEFAULT_ABI } = require('@constants')

const useDecodeEvents = () => {
    const viem = createPublicClient({
        transport: http(NETWORK_CONFIG.agd.endpoint)
    })

    const decodeLogsDefault = async log => {
        try {
            const decoded = await decodeEventLog({
                abi: DEFAULT_ABI,
                data: log.data,
                topics: log.topics
            })

            const eventABI = DEFAULT_ABI.find(item => item.name === decoded.eventName)

            return {
                eventName: decoded.eventName,
                data: log.data,
                details: Object.entries(decoded.args).map(([name, value], index) => ({
                    type: eventABI.inputs[index].type,
                    name: name,
                    value: value,
                    indexed: eventABI.inputs[index].indexed
                }))
            }
        } catch (error) {
            return {
                eventName: null,
                data: log.data,
                details: log.topics.slice(1).map(value => ({
                    type: null,
                    name: null,
                    value: value,
                    indexed: false
                }))
            }
        }
    }

    const getDecodeLogsDefault = async log => {
        try {
            const eventSignature = await viem.readContract({
                address: '0x00ae63e10e63792a8a063d36667bb870a47b4336',
                abi: ETERNAL_STORAGE_ABI,
                functionName: 'getStringValue',
                args: [log.topics[0]]
            })

            const eventName = eventSignature.split('(')[0]
            const eventKey =
                'EventABI' +
                ((eventName === 'Transfer' || eventName === 'Approval') && log.topics[3] ? 'ERC721' : '') +
                log.topics[0]

            let eventInputs = await viem.readContract({
                address: '0x00ae63e10e63792a8a063d36667bb870a47b4336',
                abi: ETERNAL_STORAGE_ABI,
                functionName: 'getStringValue',
                args: [keccak256(eventKey)]
            })

            eventInputs = JSON.parse(eventInputs)

            const decoded = await decodeEventLog({
                abi: [
                    {
                        inputs: eventInputs,
                        name: eventName,
                        type: 'event'
                    }
                ],
                data: log.data,
                topics: log.topics
            })

            if (!decoded) return await decodeLogsDefault(log)

            return {
                eventName: eventName,
                data: log.data,
                details: Object.entries(decoded.args).map(([name, value], index) => ({
                    type: eventInputs[index].type,
                    name: name,
                    value: value.toString(),
                    indexed: eventInputs[index].indexed
                }))
            }
        } catch (error) {
            return await decodeLogsDefault(log)
        }
    }

    const getDecodedLogs = async logs => {
        if (!Array.isArray(logs)) return null

        return await Promise.all(
            logs.map(async log => {
                try {
                    if (!log.abi) return await getDecodeLogsDefault(log)

                    const decoded = await decodeEventLog({
                        abi: log.abi,
                        data: log.data,
                        topics: log.topics
                    })

                    if (!decoded) return await getDecodeLogsDefault(log)

                    const eventABI = log.abi.find(item => item.name === decoded.eventName)

                    return {
                        eventName: eventABI.name,
                        data: log.data,
                        details: Object.entries(decoded.args).map(([name, value], index) => ({
                            type: eventABI.inputs[index].type,
                            name: name,
                            value: value,
                            indexed: eventABI.inputs[index].indexed
                        }))
                    }
                } catch (error) {
                    return await getDecodeLogsDefault(log)
                }
            })
        )
    }

    const getDecodedParamsWithInputABI = (logs, abi) => {
        if (!Array.isArray(logs)) return null

        return logs.map(log => {
            try {
                const decoded = decodeEventLog({
                    abi: [abi],
                    data: log.data,
                    topics: log.topics
                })
                return {
                    eventName: abi.name,
                    args: decoded.args,
                    ...decoded
                }
            } catch (error) {
                return null
            }
        })
    }

    const getDecodeAbi = (data, abi) => {
        return decodeAbiParameters(abi, data)
    }

    return { getDecodedLogs, getDecodedParamsWithInputABI, getDecodeAbi }
}

module.exports = { useDecodeEvents }
